import type {
  ComputedResults,
  ProjectionYear,
  SimulationInput,
} from "./types";
import { PRELEVEMENTS_SOCIAUX, TMI } from "./types";

const CHARGES_COLORS = [
  "oklch(0.546 0.215 262)", // primary
  "oklch(0.708 0.155 163)", // success
  "oklch(0.778 0.165 70)",  // warning
  "oklch(0.65 0.18 295)",
  "oklch(0.7 0.15 200)",
  "oklch(0.6 0.2 340)",
  "oklch(0.55 0.16 130)",
  "oklch(0.7 0.12 50)",
  "oklch(0.5 0.18 30)",
];

/** Mensualité d'un crédit (formule d'annuité standard). */
export function computeMensualite(
  montant: number,
  tauxAnnuel: number,
  dureeAnnees: number
): number {
  if (montant <= 0 || dureeAnnees <= 0) return 0;
  if (tauxAnnuel <= 0) return montant / (dureeAnnees * 12);
  const tm = tauxAnnuel / 100 / 12;
  const n = dureeAnnees * 12;
  return (montant * (tm * Math.pow(1 + tm, n))) / (Math.pow(1 + tm, n) - 1);
}

/** Tableau d'amortissement annuel : capital remboursé + intérêts par année. */
function buildAmortissementTable(
  montant: number,
  tauxAnnuel: number,
  dureeAnnees: number
): { interets: number; capital: number; restantDu: number }[] {
  const rows: { interets: number; capital: number; restantDu: number }[] = [];
  if (montant <= 0 || dureeAnnees <= 0) return rows;
  const tm = tauxAnnuel / 100 / 12;
  const n = dureeAnnees * 12;
  const mens = computeMensualite(montant, tauxAnnuel, dureeAnnees);
  let restant = montant;
  for (let y = 1; y <= dureeAnnees; y++) {
    let interetsAn = 0;
    let capitalAn = 0;
    for (let m = 0; m < 12; m++) {
      const interetMois = restant * tm;
      const capitalMois = mens - interetMois;
      interetsAn += interetMois;
      capitalAn += capitalMois;
      restant -= capitalMois;
    }
    rows.push({ interets: interetsAn, capital: capitalAn, restantDu: Math.max(0, restant) });
  }
  return rows;
}

function scoreLabelAndColor(score: number) {
  if (score < 30) return { label: "À risque", color: "danger" as const };
  if (score < 50) return { label: "Fragile", color: "warning" as const };
  if (score < 70) return { label: "Correct", color: "yellow" as const };
  if (score < 85) return { label: "Bon", color: "successLight" as const };
  return { label: "Excellent", color: "success" as const };
}

export function computeAll(input: SimulationInput): ComputedResults {
  const investissementTotal = input.prixAcquisition + input.travaux + input.mobilier;

  // Revenus
  const tauxVac = Math.max(0, Math.min(100, input.tauxVacance)) / 100;
  const revenusBrutsAnnuels = input.loyerMensuel * 12 * (1 - tauxVac);
  const revenusBrutsMensuels = revenusBrutsAnnuels / 12;

  // Crédit
  let mensualiteCredit = 0;
  let interetsAnnee1 = 0;
  let coutTotalInterets = 0;
  let assuranceAnnuelle = 0;
  let amortTable: { interets: number; capital: number; restantDu: number }[] = [];

  if (input.hasCredit && input.montantEmprunte > 0) {
    mensualiteCredit = computeMensualite(
      input.montantEmprunte,
      input.tauxAnnuel,
      input.dureeAnnees
    );
    amortTable = buildAmortissementTable(
      input.montantEmprunte,
      input.tauxAnnuel,
      input.dureeAnnees
    );
    interetsAnnee1 = amortTable[0]?.interets ?? 0;
    coutTotalInterets = amortTable.reduce((sum, r) => sum + r.interets, 0);
    assuranceAnnuelle = (input.montantEmprunte * (input.assuranceEmprunteurPct / 100));
  }

  // Charges d'exploitation (hors intérêts d'emprunt)
  // chargesTotales = somme des postes saisis/estimés visibles par l'utilisateur.
  // Les intérêts d'emprunt sont traités séparément (mensualité crédit) et n'apparaissent
  // PAS dans chargesTotales pour rester cohérent avec ce que l'utilisateur saisit.
  let chargesBreakdown: { name: string; value: number; color: string }[] = [];
  let chargesTotales: number;
  if (input.chargesMode === "simple") {
    chargesTotales = revenusBrutsAnnuels * (input.chargesPct / 100);
    chargesBreakdown = [
      { name: "Charges (mode simple)", value: chargesTotales, color: CHARGES_COLORS[0] },
    ];
  } else {
    const d = input.chargesDetail;
    const fraisGestion = (revenusBrutsAnnuels * d.fraisGestionPct) / 100;
    const items: { name: string; value: number }[] = [
      { name: "Taxe foncière", value: d.taxeFonciere },
      { name: "Assurance PNO", value: d.assurancePNO },
      { name: "Charges copro", value: d.chargesCopro },
      { name: "Entretien", value: d.entretien },
      { name: "CFE", value: d.cfe },
      { name: "Frais de gestion", value: fraisGestion },
      { name: "Comptabilité", value: d.comptabilite },
      { name: "Autres", value: d.autres },
    ];
    chargesTotales = items.reduce((s, i) => s + i.value, 0);
    chargesBreakdown = items
      .filter((i) => i.value > 0)
      .map((i, idx) => ({ ...i, color: CHARGES_COLORS[idx % CHARGES_COLORS.length] }));
  }

  // Charges décaissées = charges d'exploitation (les intérêts d'emprunt sont déjà
  // dans la mensualité crédit, ne pas les compter deux fois côté trésorerie).
  const chargesDecaissees = chargesTotales;

  const ratioCharges = revenusBrutsAnnuels > 0 ? chargesTotales / revenusBrutsAnnuels : 0;

  // Cash-flow (trésorerie réelle, intérêts comptés via mensualité)
  const mensualitesAnnuelles = mensualiteCredit * 12 + assuranceAnnuelle;
  const cashFlowAnnuel = revenusBrutsAnnuels - chargesDecaissees - mensualitesAnnuelles;
  const cashFlowMensuel = cashFlowAnnuel / 12;

  // Amortissements LMNP régime réel
  const amortissementBien = (input.prixAcquisition * 0.85) / 30;
  const amortissementMobilier = (input.mobilier + input.travaux) / 10;
  const amortissementAnnuel = amortissementBien + amortissementMobilier;

  // Charges fiscalement déductibles = charges d'exploitation + intérêts d'emprunt + assurance prêt
  // (ces deux derniers sont déductibles en LMNP réel mais ne sont PAS dans chargesTotales).
  const chargesDeductibles = chargesTotales + interetsAnnee1 + assuranceAnnuelle;

  // Résultat fiscal régime réel
  const resultatFiscalReel = revenusBrutsAnnuels - chargesDeductibles - amortissementAnnuel;
  const tauxImpotGlobal = (input.tmi ?? TMI) + PRELEVEMENTS_SOCIAUX;
  const impotsReel = Math.max(0, resultatFiscalReel) * tauxImpotGlobal;

  // Micro-BIC : abattement 50% (locations meublées classiques)
  const baseImposableMicro = revenusBrutsAnnuels * 0.5;
  const impotsMicroBIC = baseImposableMicro * tauxImpotGlobal;

  const resultatNetReel = cashFlowAnnuel - impotsReel;
  const resultatNetMicroBIC = cashFlowAnnuel - impotsMicroBIC;

  // Rendements
  const rendementBrut =
    investissementTotal > 0
      ? ((input.loyerMensuel * 12) / investissementTotal) * 100
      : 0;
  const rendementNet =
    investissementTotal > 0
      ? ((revenusBrutsAnnuels - chargesTotales) / investissementTotal) * 100
      : 0;
  const roiApport =
    input.hasCredit && input.apport > 0 ? (cashFlowAnnuel / input.apport) * 100 : null;

  // Score (0-100)
  const scoreRentabilite =
    rendementNet < 2 ? 20 : rendementNet < 4 ? 50 : rendementNet < 6 ? 75 : 95;
  const scoreCashflow =
    cashFlowMensuel < -200
      ? 10
      : cashFlowMensuel < 0
      ? 40
      : cashFlowMensuel < 200
      ? 65
      : cashFlowMensuel < 500
      ? 85
      : 100;
  const scoreChargesCalc =
    ratioCharges > 0.6 ? 30 : ratioCharges > 0.4 ? 60 : ratioCharges > 0.25 ? 85 : 100;
  const score = Math.round(
    scoreRentabilite * 0.4 + scoreCashflow * 0.4 + scoreChargesCalc * 0.2
  );
  const { label: scoreLabel, color: scoreColor } = scoreLabelAndColor(score);

  // Projection 10 ans (avec inflation loyers / charges / valorisation bien)
  const projection: ProjectionYear[] = [];
  let cashFlowCumule = 0;
  let capitalRembourseCumule = 0;
  const infLoyers = (input.inflationLoyers ?? 0) / 100;
  const infCharges = (input.inflationCharges ?? 0) / 100;
  const infBien = (input.inflationBien ?? 2) / 100;
  for (let y = 1; y <= 10; y++) {
    const row = amortTable[y - 1];
    const capitalAn = row?.capital ?? 0;
    capitalRembourseCumule += capitalAn;
    const facteurLoyers = Math.pow(1 + infLoyers, y - 1);
    const facteurCharges = Math.pow(1 + infCharges, y - 1);
    const revenusY = revenusBrutsAnnuels * facteurLoyers;
    const chargesDecaisseesY = chargesDecaissees * facteurCharges;
    const cfAnnee = revenusY - chargesDecaisseesY - mensualitesAnnuelles;
    cashFlowCumule += cfAnnee;
    const valeurBien = input.prixAcquisition * Math.pow(1 + infBien, y);
    const capitalRestantDu = row?.restantDu ?? 0;
    const patrimoineNet = valeurBien - capitalRestantDu;
    projection.push({
      annee: y,
      cashFlowAnnuel: cfAnnee,
      cashFlowCumule,
      capitalRembourseCumule,
      capitalRestantDu,
      valeurBien,
      patrimoineNet,
    });
  }

  return {
    investissementTotal,
    revenusBrutsAnnuels,
    revenusBrutsMensuels,
    mensualiteCredit,
    interetsAnnee1,
    coutTotalInterets,
    assuranceAnnuelle,
    chargesTotales,
    chargesDeductibles,
    chargesDecaissees,
    ratioCharges,
    cashFlowAnnuel,
    cashFlowMensuel,
    amortissementBien,
    amortissementMobilier,
    amortissementAnnuel,
    resultatFiscalReel,
    impotsReel,
    impotsMicroBIC,
    resultatNetReel,
    resultatNetMicroBIC,
    rendementBrut,
    rendementNet,
    roiApport,
    score,
    scoreLabel,
    scoreColor,
    chargesBreakdown,
    projection,
  };
}

export const fmtEUR = (n: number, opts: { sign?: boolean; decimals?: number } = {}) => {
  const { sign = false, decimals = 0 } = opts;
  const formatted = new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(Math.abs(n));
  if (sign && n > 0) return `+${formatted}`;
  if (n < 0) return `−${formatted}`;
  return formatted;
};

export const fmtPct = (n: number, decimals = 1) =>
  `${n.toFixed(decimals).replace(".", ",")} %`;
